import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  deleteValoracion,
  getPacienteInformeById,
  getValoracionById,
  updateValoracion,
} from '../api/valoracionFuncionalApi';
import { EMPTY_FORM_STATE } from '../model/valoracionConstants';
import { formatDateDDMMYYYY, parseNumericOrZero } from '../utils/valoracionFormatters';

export const useValoracionViewer = ({ idTipo, paciente }) => {
  const navigate = useNavigate();
  const [patientInfo, setPatientInfo] = useState({
    codigo: '',
    createdAt: '',
    nombre: '',
    apellidos: '',
    codigoInforme: '',
  });
  const [fields, setFields] = useState(EMPTY_FORM_STATE);
  const [isEditing, setIsEditing] = useState(false);
  const [openGood, setOpenGood] = useState(false);
  const [originalValues, setOriginalValues] = useState(EMPTY_FORM_STATE);

  useEffect(() => {
    if (!idTipo || !paciente) return;

    Promise.all([getPacienteInformeById(paciente), getValoracionById(idTipo)])
      .then(([pacienteData, valoracionData]) => {
        setPatientInfo({
          codigo: pacienteData.id || '',
          createdAt: formatDateDDMMYYYY(valoracionData.createdAt),
          nombre: pacienteData.firstname || '',
          apellidos: pacienteData.lastname || '',
          codigoInforme: valoracionData.codigo || '',
        });
        setFields({
          mediaDerecha: valoracionData.mediaDerecha ?? '',
          mediaIzquierda: valoracionData.mediaIzquierda ?? '',
          dsDerecha: valoracionData.dsDerecha ?? '',
          dsIzquierda: valoracionData.dsIzquierda ?? '',
        });
      })
      .catch((error) => {
        console.error('Error fetching valoracion funcional data:', error);
      });
  }, [idTipo, paciente]);

  const setField = (name, value) => {
    setFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = () => {
    setOriginalValues(fields);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFields(originalValues);
    setIsEditing(false);
  };

  const handleSave = () => {
    updateValoracion({
      codigoInforme: patientInfo.codigoInforme,
      payload: {
        mediaDerecha: parseNumericOrZero(fields.mediaDerecha),
        mediaIzquierda: parseNumericOrZero(fields.mediaIzquierda),
        dsDerecha: parseNumericOrZero(fields.dsDerecha),
        dsIzquierda: parseNumericOrZero(fields.dsIzquierda),
      },
    })
      .then(() => {
        setOpenGood(true);
        setIsEditing(false);
      })
      .catch((error) => {
        console.error('Error saving valoracion funcional:', error);
      });
  };

  const handleDelete = () => {
    if (!window.confirm('¿Está seguro de que desea eliminar este informe?')) return;

    deleteValoracion(patientInfo.codigoInforme)
      .then(() => {
        navigate('/home');
      })
      .catch((error) => {
        console.error('Error deleting valoracion funcional:', error);
      });
  };

  const handleExportPDF = () => {
    setIsEditing(false);
    const input = document.getElementById('pdf-content');
    html2canvas(input, {
      scale: 2,
      backgroundColor: null,
      useCORS: true,
      ignoreElements: (element) => element.tagName === 'BUTTON',
    })
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF();
        const imgProperties = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProperties.height * pdfWidth) / imgProperties.width;
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`valoracion_funcional_${patientInfo.nombre}${patientInfo.apellidos}_${patientInfo.createdAt}.pdf`);
      })
      .catch((error) => {
        console.error('Error exporting PDF:', error);
      });
  };

  const metrics = useMemo(() => {
    const mediaDerecha = parseFloat(fields.mediaDerecha);
    const mediaIzquierda = parseFloat(fields.mediaIzquierda);
    const maxDerecha = parseFloat(fields.dsDerecha);
    const maxIzquierda = parseFloat(fields.dsIzquierda);
    const mediaGlobal = !Number.isNaN(mediaDerecha) && !Number.isNaN(mediaIzquierda)
      ? ((mediaDerecha + mediaIzquierda) / 2).toFixed(2)
      : 'N/D';

    return [
      { label: 'Media global', value: mediaGlobal, unit: 'kg' },
      { label: 'Pico mano derecha', value: Number.isNaN(maxDerecha) ? 'N/D' : maxDerecha.toFixed(2), unit: 'kg' },
      { label: 'Pico mano izquierda', value: Number.isNaN(maxIzquierda) ? 'N/D' : maxIzquierda.toFixed(2), unit: 'kg' },
    ];
  }, [fields.mediaDerecha, fields.mediaIzquierda, fields.dsDerecha, fields.dsIzquierda]);

  return {
    patientInfo,
    fields,
    isEditing,
    openGood,
    metrics,
    setOpenGood,
    setField,
    handleEdit,
    handleCancel,
    handleSave,
    handleDelete,
    handleExportPDF,
  };
};
